import {
  useDynamicContext,
  useSwitchWallet,
  useUserUpdateRequest,
  useUserWallets,
  type Wallet,
} from '@dynamic-labs/sdk-react-core';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { Metadata } from '@repo/types-client';

import { isAirdroppingAtom } from '@claim/atoms';
import { useClaim } from '@claim/hooks/use-claim';

export const useAirdrop = (userReady: boolean) => {
  const { user } = useDynamicContext();
  const { setIsClaiming } = useClaim();
  const { updateUser } = useUserUpdateRequest();

  const userWallets = useUserWallets();
  const switchWallet = useSwitchWallet();

  const setIsAirdropping = useSetAtom(isAirdroppingAtom);

  // Extract the claim address from the user's metadata for clarity.
  const solanaClaimAddress = (user?.metadata as Metadata)?.solanaClaimAddress;

  /**
   * Resets the solanaClaimAddress in the user's metadata.
   */
  const resetMetadata = useCallback(async () => {
    try {
      await updateUser({
        metadata: {
          ...user?.metadata,
          solanaClaimAddress: null,
        },
      });
    } catch (error) {
      console.error('Error resetting metadata:', error);
    }
  }, [updateUser, user?.metadata]);

  /**
   * Switches the active wallet to the provided wallet.
   * @param wallet - The wallet to switch to.
   */
  const switchToWallet = useCallback(
    async (wallet: Wallet) => {
      try {
        await switchWallet(wallet.id);
      } catch (error) {
        console.error('Error switching wallet:', error);
      }
    },
    [switchWallet],
  );

  useEffect(() => {
    // Only proceed if the user is ready and a claim address exists.
    if (!userReady || !solanaClaimAddress) return;

    // Find the wallet that matches the claim address.
    const wallet = userWallets.find(
      (wallet) => wallet.address === solanaClaimAddress,
    );

    if (!wallet) {
      // If no wallet matches, reset the metadata.
      resetMetadata();
      return;
    }

    // Switch to the found wallet, set the claim state, and simulate the airdrop.
    switchToWallet(wallet);
    setIsClaiming(true);
    setIsAirdropping(true);
  }, [
    userReady,
    solanaClaimAddress,
    userWallets,
    resetMetadata,
    switchToWallet,
    setIsClaiming,
  ]);
};
